import { ServiceLayout } from "@/components/service/ServiceLayout";
import { ServiceFAQ } from "@/components/service/ServiceFAQ";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Code, Database, Rocket, Infinity, Server, Smartphone, Laptop } from "lucide-react";
import { ServiceCTA } from "@/components/ui/ServiceCTA";
import { serviceFAQs } from "@/data/service-faqs";

const NoCodeDevelopment = () => {
  return (
    <ServiceLayout serviceName="NoCode Development">
      {/* Hero Section with Floating Elements */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-900" />
        
        {/* Floating Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 animate-bounce">
            <Code className="w-8 h-8 text-blue-200 opacity-50" />
          </div>
          <div className="absolute top-40 right-20 animate-bounce delay-100">
            <Database className="w-8 h-8 text-blue-200 opacity-50" />
          </div>
          <div className="absolute bottom-20 left-1/4 animate-bounce delay-200">
            <Server className="w-8 h-8 text-blue-200 opacity-50" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Rapid Development with NoCode
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              We leverage cutting-edge NoCode platforms to deliver enterprise-grade solutions 
              in a fraction of the time. Get your product to market faster without compromising 
              on quality or scalability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50">
                  Start Your Project
                  <Rocket className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white bg-blue-900/30 hover:bg-blue-900/50"
                >
                  View Success Stories
                  <Infinity className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid with Hover Effects */}
      <section className="py-20 px-4 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-16 text-blue-900">
            Accelerated Development, Enterprise Quality
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Smartphone className="h-12 w-12 text-blue-600" />,
                title: "Rapid Prototyping",
                description: "We deliver functional prototypes in days instead of months using advanced NoCode platforms."
              },
              {
                icon: <Database className="h-12 w-12 text-blue-600" />,
                title: "Scalable Architecture",
                description: "Our NoCode solutions are built on enterprise-grade infrastructure, ensuring reliability and performance."
              },
              {
                icon: <Server className="h-12 w-12 text-blue-600" />,
                title: "Automated Workflows",
                description: "We implement complex business processes and automations without traditional coding bottlenecks."
              },
              {
                icon: <Laptop className="h-12 w-12 text-blue-600" />,
                title: "Seamless Integration",
                description: "Connect your existing systems and third-party services through our NoCode integration layer."
              },
              {
                icon: <Code className="h-12 w-12 text-blue-600" />,
                title: "Flexible Solutions",
                description: "Our NoCode approach allows for rapid iterations and updates as your business needs evolve."
              },
              {
                icon: <Rocket className="h-12 w-12 text-blue-600" />,
                title: "Quick Deployment",
                description: "Launch your solution faster with our streamlined NoCode development and deployment process."
              }
            ].map((feature, index) => (
              <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2">
                <div className="mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-blue-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Process Section */}
      <section className="py-20 px-4 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-16">
            Our Rapid Development Process
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { number: "01", title: "Discovery", description: "We analyze your requirements and choose the optimal NoCode stack" },
              { number: "02", title: "Architecture", description: "Design scalable solution architecture using NoCode platforms" },
              { number: "03", title: "Development", description: "Rapid implementation using our NoCode expertise" },
              { number: "04", title: "Deployment", description: "Quick launch with continuous support and iterations" }
            ].map((step, index) => (
              <div key={index} className="text-center group">
                <div className="mb-4 text-6xl font-bold text-blue-300 group-hover:text-white transition-colors">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-blue-200">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={serviceFAQs["nocode-development"]} serviceName="NoCode Development" />
      <ServiceCTA serviceName="NoCode Development" />
    </ServiceLayout>
  );
};

export default NoCodeDevelopment;